import {Box, BoxProps, Flex, FlexProps, Heading, IconButton, Text, useBreakpointValue} from '@chakra-ui/react'
import {isFunction} from 'lodash-es'
import {useTranslation} from 'next-i18next'
import React, {FC, useEffect, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import theme from 'theme'
import Quantity from '_app/components/Form/Quantity'
import {TrashIcon} from '_app/components/Icons'
import useProductOptions from '_app/hooks/useProductOptions'
import {ProductItem} from '_app/types/product'
import SpecialPrice from '_app/components/SpecialPrice'
import useProductAvailability from '_app/hooks/useProductAvailability'
import {APP_ROUTES} from '_app/routes'
import Link from '_app/components/Link/Link'
import {CloudflareImage} from '_app/components'
import {CartItem} from '../types'
import {CART_IMAGE_SIZE} from '../constants'
import {ShowItemDetails} from './ShowDetailsButton'
import {useCartItems} from '../hooks/useCartItems'
import ProductHelper from '../../../helpers/ProductHelper/ProductHelper'

interface CartProductPanelProps extends BoxProps {
  cartItem: CartItem;
  cart?: any;
  onChangeQuantity?: (itemId: string, quantity: number) => Promise<any> | false;
  onDelete?: (item: CartItem) => void;
  loading: boolean;
  setData?: any;
}

const CartProductPanel: FC<CartProductPanelProps> = ({
  cartItem,
  onChangeQuantity,
  onDelete,
  setData,
  loading,
  cart,
  ...boxProps
}) => {
  const { t } = useTranslation('cart')
  const imageThumbnailSize = useBreakpointValue(CART_IMAGE_SIZE, 'base')
  const { deleteProductHandler } = useCartItems(cart)
  const [disableQty, setDisableQty] = useState(false)
  const quantityMethods = useForm({
    [`quantity_product_${cartItem?.id}`]: cartItem?.quantity,
  })
  const { options, requiredOptions } = useProductOptions({
    product: cartItem.product,
    upsellProducts: cartItem.product.upsell_products as ProductItem[],
  })
  const isGift = cartItem?.prices?.price?.value === 0
  const quantityChangeHandler = async (value: number) => {
    if (cartItem?.quantity === value) {
      setDisableQty(true)
    } else {
      if (isFunction(onChangeQuantity)) {
        setDisableQty(true)
        const res = await onChangeQuantity(cartItem.id, value)
        setDisableQty(false)
        return res
      }
    }
  }
  const deleteHandler = () => isFunction(onDelete) && onDelete(cartItem)
  const { availableProductAmount } = useProductAvailability({
    salable_qty: cartItem?.product?.salable_qty ?? 0,
    selectedOptions: cartItem?.bundle_options
      ?.map(option => option?.values?.[0]?.uid)
      ?.filter(uid => !!uid) as string[],
    itemsOptions: cartItem?.product?.items,
    product: cartItem?.product,
  })

  const QtyDeleteDetails = (props: FlexProps) => {
    return (
      <Flex {...props} mr={{ lg: 20 }} flex="1">
        {!isGift && (
          <Flex>
            <Text
              display={{
                base: 'none',
                lg: 'inline',
              }}
              lineHeight={1.375}
              fontWeight="light"
              mr={12}
            >
              {t('QUANTITY_OF_PIECES')}
            </Text>

            <Box mr={{ base: 3, lg: 12 }}>
              <FormProvider {...quantityMethods}>
                <form>
                  <Quantity
                    mb={0}
                    setData={setData}
                    cartItem={cartItem}
                    name={`quantity_product_${cartItem?.id}`}
                    max={availableProductAmount || 1}
                    value={cartItem.quantity}
                    onStateUpdate={quantityChangeHandler}
                    onStateDelete={deleteProductHandler}
                    disableQty={disableQty}
                  />
                </form>
              </FormProvider>
            </Box>
          </Flex>
        )}

        <SpecialPrice isGift={isGift} item={cartItem} isPanel />
      </Flex>
    )
  }
  return (
    <Box
      width="100%"
      bgColor="#fff"
      border="solid 1px #ede7e1"
      p={{
        base: 4,
        lg: 6,
      }}
      {...boxProps}
    >
      <Flex flexWrap="wrap">
        <Flex
          w={{
            base: '100%',
          }}
          gap={{
            base: 3,
            lg: 6,
          }}
        >
          <Box bg="main" h={imageThumbnailSize} w={imageThumbnailSize}>
            <CloudflareImage
              src={cartItem?.product?.image?.url || ''}
              alt="Cart product"
              height={160}
              width={160}
              sizes={`
              (min-width: ${theme.breakpoints.lg}) 160px,
              90px`}
            />
          </Box>

          <Flex flex="1" flexDirection="column">
            {isGift && (
              <Flex w="100%" mb={2}>
                <Text
                  fontWeight="normal"
                  fontSize="xs"
                  textTransform="uppercase"
                  bgColor="#fcca9d"
                  py={1}
                  px={3}
                >
                  {t('GIFT', { ns: 'common' })}
                </Text>
              </Flex>
            )}
            <Flex w="100%" justifyContent="space-between" mt={1}>
              <Heading
                noOfLines={3}
                as="h3"
                fontSize="md"
                lineHeight={1.375}
                fontWeight="light"
                width={{
                  base: '100%',
                  lg: '60%',
                }}
                mb={{
                  base: 4,
                  lg: 0,
                }}
              >
                {
                  !isGift
                  && cartItem?.product?.url_key
                  && (Number(cartItem?.product?.name?.toLowerCase()?.search('lilou: #')) < 0) ? (
                  <>
                    <Link
                      href={APP_ROUTES.PRODUCTS.BY_URL_KEY(
                        cartItem?.product?.url_key
                      )}
                    >
                      {cartItem?.product?.name}
                    </Link>
                  </>
                ) : (
                  <>{cartItem?.product?.name}</>
                )}
              </Heading>

              {!isGift && (
                <IconButton
                  icon={<TrashIcon />}
                  onClick={deleteHandler}
                  variant="icon"
                  isDisabled={loading}
                  aria-label="delete"
                  ml={{
                    base: 4,
                    lg: 0,
                  }}
                  _disabled={{
                    opacity: 0.25,
                  }}
                />
              )}
            </Flex>
            <QtyDeleteDetails
              alignItems="center"
              justifyContent="space-between"
            />
          </Flex>
        </Flex>
      </Flex>
      <ShowItemDetails
        options={options}
        requiredOptions={requiredOptions}
        cartItem={cartItem}
      />
    </Box>
  )
}

export default CartProductPanel
